import React from 'react';

/**
 * This can be used for a group of elements (usually a column) that are resized together
 * and need to observe resizing. This is necessary because registering ResizeObservers is
 * pretty expensive (it takes 0.5-1 second to register a few hundred of them).
 */
export default class ResizableGroup {
  // Protected for unit tests.
  protected observers: (() => void)[] = [];
  private resizeObserver!: /* Fixme: http://tiny.cc/r4g9vz */ ResizeObserver;
  public isDefault: boolean;

  /**
   * @param isDefault Whether this ResizableGroup is the default one provided by the
   * ResizableGroupContext.
   * @param el The element to observe, if any. This should only be undefined for the default context.
   */
  public constructor(isDefault: boolean, el?: HTMLElement) {
    this.isDefault = isDefault;

    if (el !== undefined) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(el);
    }
  }

  public dispose(): void {
    this.resizeObserver.disconnect();
    this.observers = [];
  }

  public addCb(cb: () => void): void {
    this.observers.push(cb);
  }

  public removeCb(cb: () => void): void {
    const index = this.observers.indexOf(cb);
    if (index >= 0) {
      this.observers.splice(index, 1);
    } else {
      console.warn('Could not find resizable observer to delete.');
    }
  }

  // Protected for unit tests.
  protected onResize = (): void => {
    this.observers.forEach((cb) => cb());
  };
}

export const ResizableGroupContext = React.createContext<ResizableGroup>(
  new ResizableGroup(true /* isDefault */),
);

export const useResizableGroup = (): ResizableGroup => React.useContext(ResizableGroupContext);
